/**
 * ============================================================
 * System       : CSAF Alert Finance
 * Objective    : Automated Reserve Fund Notification System
 * File         : frontend/src/pages/Dashboard.jsx
 * Description  : Dashboard with stats, charts, forecast
 * ------------------------------------------------------------
 * Developer    : Chokchai Thainkhao (Head of IT)
 * Co-Author    : AI Assisted (Claude) — โครงสร้างและฟังก์ชันหลัก
 * Modified &
 *  Integrated  : Chokchai Thainkhao
 * Version      : 1.0.0
 * Date         : May 2026
 * Copyright    : © 2026 Chokchai T. All rights reserved.
 * ============================================================
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getRecords, getSettings } from '../api';
import { useToast } from '../App';

const THAI_MONTHS = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

function thaiDate(str) {
  if (!str) return '-';
  try {
    const [y, m, d] = str.split('-').map(Number);
    return `${d} ${THAI_MONTHS[m]} ${y + 543}`;
  } catch { return str; }
}

function alertStatus(record) {
  const sent = [record.alert1_sent, record.alert2_sent, record.alert3_sent].filter(Boolean).length;
  if (record.cleared) return { label: 'เคลียร์แล้ว', color: 'text-success bg-green-50' };
  if (sent === 0) return { label: 'รอ Alert 1', color: 'text-gray-500 bg-gray-50' };
  if (sent === 1) return { label: 'ส่ง Alert 1', color: 'text-warning bg-yellow-50' };
  if (sent === 2) return { label: 'ส่ง Alert 2', color: 'text-orange-600 bg-orange-50' };
  return { label: 'ส่ง Alert 3', color: 'text-danger bg-red-50' };
}

function StatCard({ label, value, color, icon }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl ${color}`}>
        {icon}
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-800">{value}</div>
        <div className="text-sm text-gray-500">{label}</div>
      </div>
    </div>
  );
}

export default function Dashboard({ setPendingCount }) {
  const [records, setRecords] = useState([]);
  const [thresholds, setThresholds] = useState([5, 10, 15]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [recRes, setRes] = await Promise.all([getRecords('all'), getSettings()]);
      setRecords(recRes.data);
      const s = setRes.data;
      setThresholds([parseInt(s.alert1_days||5), parseInt(s.alert2_days||10), parseInt(s.alert3_days||15)]);
      const pending = recRes.data.filter((r) => !r.cleared).length;
      setPendingCount(pending);
    } catch (e) {
      if (!e.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', e.response?.data?.detail || 'ไม่สามารถโหลดข้อมูลได้');
      }
    } finally {
      setLoading(false);
    }
  }, [addToast, setPendingCount]);

  useEffect(() => { loadData(); }, [loadData]);

  const pending  = useMemo(() => records.filter((r) => !r.cleared), [records]);
  const cleared  = useMemo(() => records.filter((r) =>  r.cleared), [records]);
  const totalDebt = useMemo(() => pending.reduce((s, r) => s + parseFloat(r.amount || 0), 0), [pending]);

  // รอส่ง = pending ที่ยังไม่ได้รับ alert นั้น | ส่งแล้ว = ทุก record ที่ alert นั้น sent=1
  const alertStats = [1, 2, 3].map((n) => ({
    n,
    toSend:    pending.filter((r) => !r[`alert${n}_sent`]).length,
    sent:      records.filter((r) =>  r[`alert${n}_sent`]).length,
  }));

  // สรุปตามบริษัท
  const companyStats = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const co = r.company || '(ไม่ระบุบริษัท)';
      if (!map[co]) map[co] = { total: 0, pending: 0, cleared: 0, amount: 0 };
      map[co].total++;
      if (r.cleared) map[co].cleared++;
      else { map[co].pending++; map[co].amount += parseFloat(r.amount || 0); }
    });
    return Object.entries(map).sort((a, b) => b[1].amount - a[1].amount);
  }, [records]);

  const recent = useMemo(() => [...records].slice(0, 5), [records]);

  // ย้อนหลัง 5 วัน — ส่งจริง
  const last5Days = useMemo(() => {
    const days = [];
    for (let i = 4; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push({
        key, label: `${d.getDate()} ${THAI_MONTHS[d.getMonth()+1]}`,
        a1: records.filter((r) => r.alert1_date === key).length,
        a2: records.filter((r) => r.alert2_date === key).length,
        a3: records.filter((r) => r.alert3_date === key).length,
      });
    }
    return days;
  }, [records]);

  // ล่วงหน้า 5 วัน — คาดการณ์จาก reserve_date + threshold
  const next5Days = useMemo(() => {
    const days = [];
    for (let i = 1; i <= 5; i++) {
      const d = new Date(); d.setDate(d.getDate() + i);
      const key = d.toISOString().slice(0, 10);
      const label = `${d.getDate()} ${THAI_MONTHS[d.getMonth()+1]} ${d.getFullYear()+543}`;
      let a1 = 0, a2 = 0, a3 = 0;
      pending.forEach((r) => {
        const base = new Date(r.reserve_date + 'T00:00:00');
        const due1 = new Date(base); due1.setDate(due1.getDate() + thresholds[0]);
        const due2 = new Date(base); due2.setDate(due2.getDate() + thresholds[1]);
        const due3 = new Date(base); due3.setDate(due3.getDate() + thresholds[2]);
        if (!r.alert1_sent && due1.toISOString().slice(0,10) === key) a1++;
        if ( r.alert1_sent && !r.alert2_sent && due2.toISOString().slice(0,10) === key) a2++;
        if ( r.alert2_sent && !r.alert3_sent && due3.toISOString().slice(0,10) === key) a3++;
      });
      days.push({ key, label, a1, a2, a3 });
    }
    return days;
  }, [pending, thresholds]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="รายการทั้งหมด"      value={records.length} color="bg-primary" icon="📋" />
        <StatCard label="รอดำเนินการ"         value={pending.length} color="bg-warning" icon="⏳" />
        <StatCard label="เคลียร์แล้ว"         value={cleared.length} color="bg-success" icon="✅" />
        <StatCard label="ยอดค้างรวม (บาท)"   value={totalDebt.toLocaleString('th-TH', { minimumFractionDigits: 2 })} color="bg-danger" icon="💰" />
      </div>

      {/* Alert stage + Company summary */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Alert stage summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="font-semibold text-gray-700 mb-4">สรุปสถานะ Alert (รายการที่ยังไม่เคลียร์)</h2>
          <div className="flex flex-col gap-3">
            {alertStats.map(({ n, toSend, sent }) => {
              const colors = [
                'border-yellow-300 bg-yellow-50',
                'border-orange-300 bg-orange-50',
                'border-red-300 bg-red-50',
              ];
              const textColors = ['text-yellow-700', 'text-orange-700', 'text-red-700'];
              return (
                <div key={n} className={`rounded-lg border px-4 py-3 flex items-center gap-4 ${colors[n-1]}`}>
                  <div className={`text-sm font-bold w-20 shrink-0 ${textColors[n-1]}`}>Alert ครั้งที่ {n}</div>
                  <div className="flex-1 flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-800">{toSend}</div>
                      <div className="text-xs text-gray-500">รอส่ง</div>
                    </div>
                    <div className={`text-2xl font-light ${textColors[n-1]}`}>/</div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-800">{sent}</div>
                      <div className="text-xs text-gray-500">ส่งแล้ว</div>
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-2 ml-2">
                      <div
                        className={`h-2 rounded-full transition-all ${n===1?'bg-yellow-400':n===2?'bg-orange-400':'bg-red-400'}`}
                        style={{ width: `${(sent + toSend) > 0 ? (sent / (sent + toSend)) * 100 : 0}%` }}
                      />
                    </div>
                    <div className={`text-sm font-semibold w-12 text-right ${textColors[n-1]}`}>
                      {sent + toSend > 0 ? Math.round((sent / (sent + toSend)) * 100) : 0}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Company summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-700">สรุปตามบริษัท</h2>
          </div>
          {companyStats.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">ไม่มีข้อมูล</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {companyStats.map(([co, s]) => (
                <div key={co} className="px-5 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 text-sm truncate">{co}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      ทั้งหมด {s.total} รายการ &nbsp;·&nbsp;
                      <span className="text-warning">รอ {s.pending}</span>
                      &nbsp;·&nbsp;
                      <span className="text-success">เคลียร์ {s.cleared}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold text-danger text-sm">
                      {s.amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs text-gray-400">ยอดค้าง (บาท)</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Next 5 days + Recent records */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <AlertDayTable title="Alert ที่คาดว่าจะส่ง 5 วันข้างหน้า" days={next5Days} upcoming />

        {/* Recent records */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-700">รายการล่าสุด</h2>
          <span className="text-xs text-gray-400">5 รายการล่าสุด</span>
        </div>
        {recent.length === 0 ? (
          <div className="text-center py-12 text-gray-400">ยังไม่มีรายการ</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recent.map((r) => {
              const st = alertStatus(r);
              return (
                <div key={r.id} className="px-5 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-800 text-sm truncate">{r.name}</div>
                      <div className="text-xs text-gray-400">{r.company || '-'}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${st.color}`}>
                      {st.label}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-400">{thaiDate(r.reserve_date)}</span>
                    <span className="text-sm font-semibold text-gray-700">
                      {parseFloat(r.amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function AlertDayTable({ title, days, todayKey, upcoming }) {
  const Badge = ({ val, color }) => val > 0
    ? <span className={`inline-block min-w-[26px] px-2 py-0.5 rounded-full font-bold text-xs ${color}`}>{val}</span>
    : <span className="text-gray-300 text-xs">—</span>;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="font-semibold text-gray-700">{title}</h2>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-xs border-b border-gray-100">
          <tr>
            <th className="px-4 py-2.5 text-left">วันที่</th>
            <th className="px-4 py-2.5 text-center"><span className="text-yellow-700">Alert 1</span></th>
            <th className="px-4 py-2.5 text-center"><span className="text-orange-700">Alert 2</span></th>
            <th className="px-4 py-2.5 text-center"><span className="text-red-700">Alert 3</span></th>
            <th className="px-4 py-2.5 text-center text-gray-400">รวม</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {days.map((day) => {
            const total = day.a1 + day.a2 + day.a3;
            const isToday = day.key === todayKey;
            return (
              <tr key={day.key} className={`hover:bg-gray-50 transition-colors ${isToday ? 'bg-primary/5' : upcoming && total > 0 ? 'bg-blue-50/30' : ''}`}>
                <td className="px-4 py-2.5 font-medium text-gray-700 text-sm">
                  {day.label}
                  {isToday && <span className="ml-2 text-[10px] text-primary font-semibold">วันนี้</span>}
                </td>
                <td className="px-4 py-2.5 text-center"><Badge val={day.a1} color="bg-yellow-100 text-yellow-700" /></td>
                <td className="px-4 py-2.5 text-center"><Badge val={day.a2} color="bg-orange-100 text-orange-700" /></td>
                <td className="px-4 py-2.5 text-center"><Badge val={day.a3} color="bg-red-100 text-red-700" /></td>
                <td className="px-4 py-2.5 text-center font-semibold text-gray-600 text-sm">
                  {total > 0 ? total : <span className="text-gray-300 font-normal text-xs">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
